import { cn } from '@/lib/utils'
import { ReactElement } from 'react'
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/base_components/ui/tooltip'

type TooltipSide = 'top' | 'right' | 'bottom' | 'left'
type TooltipAlign = 'start' | 'center' | 'end'

// Mirrors Bootstrap's Placement type so callers don't need to change
export type Placement =
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'left'
  | 'left-start'
  | 'left-end'

type CustomTooltipProps = {
  direction?: Placement
  content: string | React.ReactNode
  showDelay?: number
  /** @deprecated No longer used — Radix does not support hide delay */
  hideDelay?: number
  /** @deprecated No longer used — styling is handled via Tailwind */
  opacity?: string
  targetElement: ReactElement
  /** @deprecated No longer used — styling is handled via Tailwind */
  bsPrefixClass?: string
  /** [alignOffset, sideOffset] — maps to Radix align/side offsets */
  offset?: number[]
  /** Override the default dark tooltip styling */
  tooltipClassName?: string
  /** Override the arrow fill color — defaults to black to match the default dark tooltip */
  arrowClassName?: string
}

function parsePlacement(
  direction: Placement
): {
  side?: TooltipSide
  align?: TooltipAlign
} {
  const [side, align] = direction.split('-')

  if (side === 'auto') {
    return { align: align as TooltipAlign | undefined }
  }

  return {
    side: side as TooltipSide,
    align: align as TooltipAlign | undefined,
  }
}

// Direction supports Bootstrap placement values: top, bottom, left, right (with -start/-end variants) and auto
// drop-shadow instead of box-shadow so the shadow extends through the TooltipArrow SVG
// z-[1070] matches Bootstrap tooltip stacking and stays above BaseModal (z-index: 1050)
const DEFAULT_TOOLTIP_CLASS =
  '~z-[1070] ~bg-black ~border-black ~rounded-[6px] ~shadow-none ~drop-shadow-[0px_4px_12px_rgba(0,0,0,0.12)] ~text-[16px] ~font-medium ~font-roboto ~max-w-[300px] ~p-[24px] ~text-left ~text-white'

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  direction = 'auto',
  content,
  showDelay = 250,
  targetElement,
  offset = [],
  tooltipClassName,
  arrowClassName,
}) => {
  const { side, align } = parsePlacement(direction)
  // Only pass offsets when they have values — Floating UI expects numbers, undefined causes NaN in positioning
  const sideOffset = offset[1] !== undefined ? offset[1] : undefined
  const alignOffset = offset[0] !== undefined ? offset[0] : undefined

  return (
    <TooltipProvider delayDuration={showDelay}>
      <Tooltip>
        <TooltipTrigger asChild>{targetElement}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          {...(sideOffset !== undefined && { sideOffset })}
          {...(alignOffset !== undefined && { alignOffset })}
          style={{ overflow: 'visible' }}
          className={cn(DEFAULT_TOOLTIP_CLASS, tooltipClassName)}
        >
          {content}
          <TooltipArrow
            width={11}
            height={6}
            className={cn('~fill-black', arrowClassName)}
          />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default CustomTooltip
